import { useState } from 'react';
import { Button } from '../ui/button';
import { Popover, PopoverContent, PopoverTrigger } from '../ui/popover';
import { Command, CommandEmpty, CommandGroup, CommandInput, CommandItem, CommandList } from '../ui/command';
import { User, ChevronsUpDown, Check } from 'lucide-react';

/**
 * Searchable contact picker used by the New Request Wizard. Renders a
 * shadcn Popover + Command combo so contacts can be filtered by name,
 * email, or company. Selecting a contact emits its `contact_id` through
 * `onChange`; the empty option clears the link so the user can type the
 * recipient manually.
 *
 * Extracted from pages/SignaturesHub.jsx.
 */
export function ContactPicker({ contacts, value, onChange }) {
  const [open, setOpen] = useState(false);
  const selected = contacts.find((c) => c.contact_id === value);
  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <Button
          variant="outline"
          role="combobox"
          aria-expanded={open}
          className="w-full justify-between font-normal"
          data-testid="contact-picker-trigger"
        >
          <span className="flex items-center gap-2 min-w-0 truncate">
            <User className="w-4 h-4 text-muted-foreground flex-shrink-0" />
            {selected ? (
              <>
                <span className="truncate">{selected.name}</span>
                <span className="text-xs text-muted-foreground">· {selected.email || 'no email'}</span>
              </>
            ) : (
              <span className="text-muted-foreground">— Select a contact —</span>
            )}
          </span>
          <ChevronsUpDown className="h-4 w-4 opacity-50" />
        </Button>
      </PopoverTrigger>
      <PopoverContent className="w-[var(--radix-popover-trigger-width)] p-0" align="start">
        <Command>
          <CommandInput placeholder="Search by name or email…" className="h-9" />
          <CommandList className="max-h-72">
            <CommandEmpty>No contacts found.</CommandEmpty>
            <CommandGroup>
              <CommandItem onSelect={() => { onChange(''); setOpen(false); }}>
                <span className="text-muted-foreground italic">— No contact (enter recipient manually) —</span>
              </CommandItem>
              {contacts.map((c) => (
                <CommandItem
                  key={c.contact_id}
                  value={`${c.name} ${c.email || ''} ${c.company || ''}`}
                  onSelect={() => { onChange(c.contact_id); setOpen(false); }}
                  data-testid={`contact-option-${c.contact_id}`}
                >
                  <Check className={`mr-2 h-3.5 w-3.5 ${value === c.contact_id ? 'opacity-100' : 'opacity-0'}`} />
                  <div className="flex flex-col min-w-0">
                    <span className="truncate font-medium">{c.name}</span>
                    {c.email && <span className="text-[11px] text-muted-foreground truncate">{c.email}</span>}
                  </div>
                  {c.contact_type && (
                    <span className="ml-auto text-[10px] text-muted-foreground capitalize">{c.contact_type}</span>
                  )}
                </CommandItem>
              ))}
            </CommandGroup>
          </CommandList>
        </Command>
      </PopoverContent>
    </Popover>
  );
}

export default ContactPicker;
